import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Preloader } from 'konsta/react';
import { Check, CheckCheck, Image as ImageIcon, Mic, Paperclip, Send, Square, Video, AlertCircle } from 'lucide-react';
import { api, actionId, ApiError, type ChatMessage, type Conversation, type MessageType } from '../api';
import { Avatar, BottomSheet, EmptyState, TopBar, timeLabel, useToast } from '../components/ui';
import { Loading } from './client/Discover';
import { getSocket, type MessageEvent } from '../realtime';
import { haptic } from '../telegram';
import { useErrorText, useLang, useT } from '../i18n';

/** Chat list, like Telegram: photo, name, last message, time, unread badge. */
export function ChatsTab({ openChat, onUnread }: { openChat: (id: string) => void; onUnread?: (n: number) => void }) {
  const t = useT();
  const lang = useLang();
  const [items, setItems] = useState<Conversation[] | null>(null);
  const load = useCallback(() => {
    api.conversations().then((r) => {
      setItems(r);
      onUnread?.(r.reduce((n, c) => n + c.unread, 0));
    }).catch(() => undefined);
  }, []);
  useEffect(() => {
    load();
    const s = getSocket();
    s?.on('chat:message', load);
    s?.on('chat:read', load);
    const id = window.setInterval(load, 30_000);
    return () => {
      s?.off('chat:message', load);
      s?.off('chat:read', load);
      window.clearInterval(id);
    };
  }, [load]);

  if (!items) return <Loading />;
  if (!items.length) return <EmptyState icon="💬" text={t('chat.empty_list')} />;
  const preview = (c: Conversation) => {
    const m = c.lastMessage;
    if (!m) return t('chat.start');
    const body = m.type === 'TEXT' ? m.text : m.type === 'PHOTO' ? `📷 ${t('chat.photo')}` : m.type === 'VOICE' ? `🎤 ${t('chat.voice')}` : `🎥 ${t('chat.video')}`;
    return (m.mine ? '✓ ' : '') + body;
  };
  return (
    <div className="pb-28">
      {items.map((c) => (
        <button key={c.id} onClick={() => openChat(c.id)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left active:bg-tg-secondary">
          <Avatar src={c.other.photo} name={c.other.name} size={54} />
          <div className="min-w-0 flex-1 border-b border-black/5 pb-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate font-semibold">{c.other.name}</span>
              <span className={`shrink-0 text-xs ${c.unread ? 'font-semibold text-tg-link' : 'text-tg-hint'}`}>{timeLabel(c.lastMessage?.at ?? c.activityAt, lang)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className={`truncate text-sm ${c.canSend ? 'text-tg-hint' : 'italic text-tg-hint'}`}>{c.canSend ? preview(c) : t('chat.closed')}</span>
              {c.unread > 0 && <span className="shrink-0 rounded-full bg-tg-button px-2 py-0.5 text-xs font-bold text-tg-buttonText">{c.unread}</span>}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

type Pending = ChatMessage & { status: 'sending' | 'failed'; retry: () => void };
type Item = ChatMessage | Pending;

const drafts = new Map<string, string>();
const MEDIA_PURPOSE: Record<Exclude<MessageType, 'TEXT'>, 'chat_photo' | 'chat_voice' | 'chat_video'> = {
  PHOTO: 'chat_photo',
  VOICE: 'chat_voice',
  VIDEO: 'chat_video',
};

/** One conversation: bubbles, live updates, text, photo, voice and video. */
export function ChatScreen({ id }: { id: string }) {
  const t = useT();
  const lang = useLang();
  const toast = useToast();
  const errText = useErrorText();
  const [title, setTitle] = useState('');
  const [canSend, setCanSend] = useState(true);
  const [items, setItems] = useState<Item[] | null>(null);
  const [text, setText] = useState(drafts.get(id) ?? '');
  const [typing, setTyping] = useState(false);
  const [attach, setAttach] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);

  const merge = useCallback((incoming: ChatMessage[]) => {
    setItems((cur) => {
      const list = [...(cur ?? [])];
      for (const m of incoming) {
        const i = list.findIndex((x) => x.id === m.id || (x.mine && x.clientMsgId === m.clientMsgId));
        if (i >= 0) list[i] = m;
        else list.push(m);
      }
      return list.sort((a, b) => ('status' in a ? 1 : 0) - ('status' in b ? 1 : 0) || Number(a.seq) - Number(b.seq));
    });
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .messages(id)
      .then((r) => {
        if (!alive) return;
        setTitle(r.conversation.otherName);
        setCanSend(r.conversation.canSend);
        setItems(r.messages);
        api.markRead(id).catch(() => undefined);
      })
      .catch(() => setItems([]));

    const s = getSocket();
    const join = () => s?.emit('chat:join', id);
    join();
    s?.on('connect', join);
    const onMsg = (e: MessageEvent) => {
      if (e.conversationId !== id) return;
      merge([e.message]);
      if (!e.message.mine) {
        setTyping(false);
        api.markRead(id).catch(() => undefined);
      }
    };
    const onRead = (e: { conversationId: string }) => {
      if (e.conversationId === id) setItems((cur) => cur?.map((m) => (m.mine && !m.readAt ? { ...m, readAt: new Date().toISOString() } : m)) ?? cur);
    };
    let typingTimer = 0;
    const onTyping = (e: { conversationId: string }) => {
      if (e.conversationId !== id) return;
      setTyping(true);
      window.clearTimeout(typingTimer);
      typingTimer = window.setTimeout(() => setTyping(false), 4000);
    };
    s?.on('chat:message', onMsg);
    s?.on('chat:read', onRead);
    s?.on('chat:typing', onTyping);
    return () => {
      alive = false;
      s?.emit('chat:leave', id);
      s?.off('connect', join);
      s?.off('chat:message', onMsg);
      s?.off('chat:read', onRead);
      s?.off('chat:typing', onTyping);
      window.clearTimeout(typingTimer);
    };
  }, [id, merge]);

  useLayoutEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [items?.length, typing]);

  /** Shows the message at once, then sends it; on failure it stays with "tap to retry". */
  const deliver = useCallback(
    (type: MessageType, prepare: () => Promise<{ text?: string; mediaKey?: string; durationSec?: number }>, preview: Partial<ChatMessage>) => {
      const clientMsgId = actionId();
      const temp: Pending = {
        id: `tmp-${clientMsgId}`,
        seq: String(Number.MAX_SAFE_INTEGER),
        clientMsgId,
        type,
        text: null,
        mediaUrl: null,
        durationSec: null,
        mine: true,
        createdAt: new Date().toISOString(),
        readAt: null,
        status: 'sending',
        retry: () => undefined,
        ...preview,
      };
      let prepared: { text?: string; mediaKey?: string; durationSec?: number } | null = null;
      const attempt = async () => {
        setItems((cur) => cur?.map((m) => (m.clientMsgId === clientMsgId && 'status' in m ? { ...m, status: 'sending' } : m)) ?? cur);
        try {
          prepared ??= await prepare();
          const saved = await api.send(id, { clientMsgId, type, ...prepared });
          merge([saved]);
        } catch (e) {
          if (e instanceof ApiError && ['CHAT_CLOSED', 'MEDIA_DISABLED', 'CHAT_DISABLED'].includes(e.code)) {
            toast(errText(e));
            if (e.code === 'CHAT_CLOSED') setCanSend(false);
          }
          setItems((cur) => cur?.map((m) => (m.clientMsgId === clientMsgId && 'status' in m ? { ...m, status: 'failed' } : m)) ?? cur);
        }
      };
      temp.retry = attempt;
      setItems((cur) => [...(cur ?? []), temp]);
      attempt();
    },
    [id, merge],
  );

  const sendText = () => {
    const body = text.trim();
    if (!body) return;
    setText('');
    drafts.delete(id);
    haptic();
    deliver('TEXT', async () => ({ text: body }), { text: body });
  };

  const sendFile = (type: 'PHOTO' | 'VIDEO' | 'VOICE', file: Blob, name: string, durationSec?: number) => {
    const local = URL.createObjectURL(file);
    deliver(
      type,
      async () => {
        const up = await api.upload(MEDIA_PURPOSE[type], file, name);
        return { mediaKey: up.key, durationSec };
      },
      { mediaUrl: local, durationSec: durationSec ?? null },
    );
  };

  const onType = (v: string) => {
    setText(v);
    drafts.set(id, v);
    if (Date.now() - lastTyping.current > 2500) {
      lastTyping.current = Date.now();
      getSocket()?.emit('chat:typing', id);
    }
  };

  return (
    <div className="flex h-full flex-col bg-tg-secondary">
      <TopBar title={<span className="flex flex-col leading-tight">{title}{typing && <span className="text-xs font-normal text-tg-link">{t('chat.typing')}</span>}</span>} />
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {!items && <Loading />}
        {items && !items.length && <p className="mx-auto mt-10 max-w-[260px] rounded-2xl bg-tg-bg p-4 text-center text-sm text-tg-hint">{t('chat.start')}</p>}
        {items?.map((m) => <Bubble key={m.id} m={m} lang={lang} />)}
        <div ref={bottom} />
      </div>
      {canSend ? (
        <div className="flex items-end gap-2 border-t border-black/5 bg-tg-bg px-2 pb-5 pt-2">
          <button onClick={() => setAttach(true)} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-tg-hint active:bg-tg-secondary" aria-label="attach">
            <Paperclip size={22} />
          </button>
          <textarea
            rows={1}
            value={text}
            onChange={(e) => onType(e.target.value)}
            placeholder={t('chat.placeholder')}
            className="max-h-32 min-h-[44px] flex-1 resize-none rounded-3xl bg-tg-secondary px-4 py-2.5 text-[16px] outline-none"
          />
          {text.trim() ? (
            <button onClick={sendText} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-tg-button text-tg-buttonText" aria-label="send">
              <Send size={20} />
            </button>
          ) : (
            <VoiceButton onDone={(blob, sec, ext) => sendFile('VOICE', blob, `voice.${ext}`, sec)} onError={() => toast(t('chat.mic_denied'))} />
          )}
        </div>
      ) : (
        <div className="bg-tg-bg px-4 pb-6 pt-3 text-center text-sm text-tg-hint">{t('chat.closed')}</div>
      )}
      <input
        ref={photoInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) sendFile('PHOTO', f, f.name);
        }}
      />
      <input
        ref={videoInput}
        type="file"
        accept="video/mp4,video/quicktime,video/webm"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) sendFile('VIDEO', f, f.name);
        }}
      />
      <BottomSheet open={attach} onClose={() => setAttach(false)}>
        <div className="grid grid-cols-2 gap-3 pb-2">
          <button onClick={() => (setAttach(false), photoInput.current?.click())} className="flex flex-col items-center gap-2 rounded-2xl bg-tg-secondary py-5 font-medium">
            <ImageIcon size={28} className="text-tg-link" /> {t('chat.photo')}
          </button>
          <button onClick={() => (setAttach(false), videoInput.current?.click())} className="flex flex-col items-center gap-2 rounded-2xl bg-tg-secondary py-5 font-medium">
            <Video size={28} className="text-tg-link" /> {t('chat.video')}
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}

function Bubble({ m, lang }: { m: Item; lang: string }) {
  const t = useT();
  const pending = 'status' in m ? m : null;
  const mine = m.mine;
  return (
    <div className={`mb-1.5 flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        onClick={pending?.status === 'failed' ? pending.retry : undefined}
        className={`max-w-[80%] overflow-hidden rounded-2xl shadow-sm ${mine ? 'rounded-br-md bg-[var(--h-bubble)] text-[var(--h-bubble-text)]' : 'rounded-bl-md bg-tg-bg'} ${
          m.type === 'TEXT' || m.type === 'VOICE' ? 'px-3 py-2' : 'p-1'
        }`}
      >
        {m.type === 'TEXT' && <p className="whitespace-pre-wrap break-words text-[16px]">{m.text}</p>}
        {m.type === 'PHOTO' && m.mediaUrl && <img src={m.mediaUrl} alt="" className="max-h-80 rounded-xl object-cover" />}
        {m.type === 'VIDEO' && m.mediaUrl && <video src={m.mediaUrl} controls playsInline preload="metadata" className="max-h-80 rounded-xl" />}
        {m.type === 'VOICE' && m.mediaUrl && (
          <div className="flex min-w-[200px] items-center gap-2">
            <Mic size={18} className="shrink-0 text-tg-link" />
            <audio src={m.mediaUrl} controls preload="none" className="h-9 w-full" />
          </div>
        )}
        <div className={`flex items-center justify-end gap-1 text-[11px] ${mine ? 'text-[var(--h-bubble-meta)]' : 'text-tg-hint'} ${m.type === 'PHOTO' || m.type === 'VIDEO' ? 'px-2 pb-1' : ''}`}>
          {pending?.status === 'failed' ? (
            <span className="flex items-center gap-1 text-red-600">
              <AlertCircle size={12} /> {t('chat.failed')}
            </span>
          ) : (
            <>
              {timeLabel(m.createdAt, lang)}
              {mine && (pending ? <Preloader className="h-3 w-3" /> : m.readAt ? <CheckCheck size={14} className="text-sky-500" /> : <Check size={14} />)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Tap to start recording, tap ■ to send. Uses whatever audio format the phone supports. */
function VoiceButton({ onDone, onError }: { onDone: (b: Blob, sec: number, ext: string) => void; onError: () => void }) {
  const t = useT();
  const [rec, setRec] = useState<MediaRecorder | null>(null);
  const [secs, setSecs] = useState(0);
  const chunks = useRef<Blob[]>([]);
  const started = useRef(0);
  const timer = useRef<number>();

  const start = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') throw new Error('unsupported');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'].find((x) => MediaRecorder.isTypeSupported?.(x));
      const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      chunks.current = [];
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        window.clearInterval(timer.current);
        const mime = r.mimeType || 'audio/webm';
        const blob = new Blob(chunks.current, { type: mime });
        const sec = Math.round((Date.now() - started.current) / 1000);
        setRec(null);
        setSecs(0);
        if (blob.size > 0 && sec >= 1) onDone(blob, sec, mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm');
      };
      started.current = Date.now();
      r.start();
      setRec(r);
      haptic();
      timer.current = window.setInterval(() => {
        const s = Math.round((Date.now() - started.current) / 1000);
        setSecs(s);
        if (s >= 300 && r.state === 'recording') r.stop(); // 5 minute limit
      }, 500);
    } catch {
      onError();
    }
  };

  if (rec)
    return (
      <button onClick={() => rec.stop()} className="flex h-11 shrink-0 items-center gap-2 rounded-full bg-red-500 px-4 text-white" aria-label={t('chat.recording')}>
        <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
        <span className="text-sm tabular-nums">{Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}</span>
        <Square size={16} className="fill-white" />
      </button>
    );
  return (
    <button onClick={start} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-tg-button text-tg-buttonText" aria-label={t('chat.voice')}>
      <Mic size={20} />
    </button>
  );
}
